import { queryOptions } from '@tanstack/react-query';
import { client as qontoClient } from '#queries/utils';
import { persons as kycAcceptance } from '#proto/index.qonto.kycb.acceptance';
import type { KycResponse } from './types';

interface KycQueriesItemParams {
  /** Market identifier (e.g. legal country) for cache isolation and the `market` query parameter. */
  market?: string;
  /**
   * Organization ID for the `X-Qonto-Organization-Id` header.
   * Only needed when session storage is not yet populated (e.g. during login).
   * Intentionally excluded from query keys — used for auth only, not cache differentiation.
   */
  organizationId?: string;
}

/** Query key factory for KYC acceptance queries. */
export const kycQueryKeys = {
  all: ['kyc-acceptance'] as const,
  /** Query key for a single person's KYC status. Only {@link KycQueriesItemParams.market} affects the key. */
  item: (params: KycQueriesItemParams = {}) =>
    [...kycQueryKeys.all, 'status', ...(params.market ? [params.market] : [])] as const,
};

export async function fetchKycAcceptance({
  market,
  organizationId,
}: KycQueriesItemParams = {}): Promise<KycResponse> {
  const response = await qontoClient<KycResponse>(`/kycb-acceptance/v1/persons/me`, {
    method: 'GET',
    ...(market ? { params: { market } } : {}),
    ...(organizationId?.trim() ? { headers: { 'X-Qonto-Organization-Id': organizationId } } : {}),
  });

  return kycAcceptance.firstparty.v1.ApiGetPersonAcceptanceResponse.fromJSON(response);
}

/** Query option factories for KYC acceptance data. */
export const kycQueries = {
  all: kycQueryKeys.all,
  /**
   * Query options for the current user's KYC acceptance status
   * via `GET /kycb-acceptance/v1/persons/me`.
   *
   * @see {@link KycQueriesItemParams} for parameter details.
   */
  item: (params: KycQueriesItemParams = {}) =>
    queryOptions({
      queryKey: kycQueryKeys.item(params),
      queryFn: () => fetchKycAcceptance(params),
    }),
};
